// Rules of "Gestionar" (design RolloManagePrep, S5, S6 and S9), mirrored from RolloGestionService so the asesor
// sees every error before the POST. The server validates everything again. Motivo and estado lists are the codes
// the server accepts, not business data: the gestión itself always comes from GET gestiones/{id}.
import {
  CANAL_LABELS,
  ESTADO_LABELS,
  MOTIVO_CANCELACION_LABELS,
  MOTIVO_NO_ENTREGA_LABELS,
  codeOf,
  formatDate,
  formatRollos,
  formatShortDate,
  formatTime,
  labelFor,
} from "../rolloFormat";

export { createCorrelationId } from "../nuevaGestion/nuevaGestionModel";

// Steps of the stepper. CANCELADO is not a step: the stepper goes grey.
export const STEPS = ["SOLICITADO", "EN_PREPARACION", "EN_CAMINO", "ENTREGADO"];
export const CLOSED_ESTADOS = ["ENTREGADO", "CANCELADO"];

export const ACCIONES = {
  PREPARAR: "PREPARAR",
  ENVIAR: "ENVIAR",
  ENTREGAR: "ENTREGAR",
  NO_ENTREGADO: "NO_ENTREGADO",
  CANCELAR: "CANCELAR",
};

// Same limits as RolloGestionService.
export const LIMITS = {
  cantidadMin: 1,
  cantidadMax: 50,
  cadete: 150,
  recibio: 150,
  observacion: 1000,
};

const toOptions = (labels) => Object.entries(labels).map(([value, label]) => ({ value, label }));
export const MOTIVO_NO_ENTREGA_OPTIONS = toOptions(MOTIVO_NO_ENTREGA_LABELS);
export const MOTIVO_CANCELACION_OPTIONS = toOptions(MOTIVO_CANCELACION_LABELS);
const MOTIVO_OTRO = "OTRO";

// Form keys in the order they appear on screen, with the id suffix of their control: the first invalid one gets
// the focus. The cancel confirmation has its own form and ids.
export const FIELD_IDS = {
  cadete: "cadete",
  fechaSalida: "fecha-salida",
  horaSalida: "hora-salida",
  fechaEntrega: "fecha-entrega",
  cantidad: "cantidad",
  recibio: "recibio",
  motivo: "motivo",
  nuevaFecha: "nueva-fecha",
  observacion: "observacion",
};
export const CANCEL_FIELD_IDS = {
  motivo: "motivo",
  observacion: "observacion",
};

export const fieldId = (key) => `crm-rollo-gestionar-${FIELD_IDS[key]}`;
export const cancelFieldId = (key) => `crm-rollo-cancelar-${CANCEL_FIELD_IDS[key]}`;
export const errorIdOf = (id) => `${id}-error`;

export const firstErrorKey = (errors, ids = FIELD_IDS) => Object.keys(ids).find((key) => errors[key]) || null;

export const isClosed = (estado) => CLOSED_ESTADOS.includes(codeOf(estado));

// Action of the panel for the estado; EN_CAMINO has two outcomes chosen with the SegmentedControl.
export const accionFor = (estado, enCaminoTab) => {
  switch (codeOf(estado)) {
    case "SOLICITADO":
      return ACCIONES.PREPARAR;
    case "EN_PREPARACION":
      return ACCIONES.ENVIAR;
    case "EN_CAMINO":
      return enCaminoTab === ACCIONES.NO_ENTREGADO ? ACCIONES.NO_ENTREGADO : ACCIONES.ENTREGAR;
    default:
      return null;
  }
};

export const PRIMARY_LABELS = {
  [ACCIONES.PREPARAR]: "Pasar a En preparación",
  [ACCIONES.ENVIAR]: "Enviar con cadete",
  [ACCIONES.ENTREGAR]: "Registrar entrega",
  [ACCIONES.NO_ENTREGADO]: "Confirmar no entrega",
};

// One form for every action: what the asesor typed survives a reload of the gestión (conflict) and the switch
// between the two outcomes of En camino keeps the shared fields. Fecha de salida and fecha real start today and
// the delivered quantity starts with the requested one (design).
export const buildInitialForm = (gestion, today) => ({
  cadete: "",
  fechaSalida: today,
  horaSalida: "",
  fechaEntrega: today,
  cantidad: gestion?.cantidadSolicitada ? String(gestion.cantidadSolicitada) : "",
  recibio: "",
  motivo: "",
  nuevaFecha: "",
  observacion: "",
});

export const EMPTY_CANCEL_FORM = { motivo: "", observacion: "" };

const text = (value) => String(value ?? "").trim();
const tooLong = (value, max) => text(value).length > max;
const textOrNull = (value) => text(value) || null;

const checkObservacion = (errors, observacion, required, requiredMessage) => {
  if (required && !text(observacion)) {
    errors.observacion = requiredMessage;
  } else if (tooLong(observacion, LIMITS.observacion)) {
    errors.observacion = `Acortá la observación a ${LIMITS.observacion} caracteres como máximo`;
  }
};

// Quantity typed in the delivery form as a whole number, or null.
export const parseCantidad = (value) => {
  const raw = text(value);
  const number = Number(raw);
  return raw && Number.isInteger(number) ? number : null;
};

// ISO dates (YYYY-MM-DD) compare as text.
export const validateAccion = (accion, form, today) => {
  const errors = {};

  if (accion === ACCIONES.ENVIAR) {
    if (!text(form.cadete)) errors.cadete = "Ingresá el nombre del cadete";
    else if (tooLong(form.cadete, LIMITS.cadete)) errors.cadete = `Acortá el nombre a ${LIMITS.cadete} caracteres como máximo`;
    if (!form.fechaSalida) errors.fechaSalida = "Ingresá la fecha de salida";
    if (!form.horaSalida) errors.horaSalida = "Ingresá la hora de salida";
  }

  if (accion === ACCIONES.ENTREGAR) {
    if (!form.fechaEntrega) errors.fechaEntrega = "Ingresá la fecha de entrega";
    else if (today && form.fechaEntrega > today) errors.fechaEntrega = "La fecha no puede ser futura";
    const cantidad = parseCantidad(form.cantidad);
    if (!text(form.cantidad)) errors.cantidad = "Ingresá la cantidad entregada";
    else if (cantidad === null || cantidad < LIMITS.cantidadMin || cantidad > LIMITS.cantidadMax) {
      errors.cantidad = `Ingresá un número entero entre ${LIMITS.cantidadMin} y ${LIMITS.cantidadMax}`;
    }
    if (!text(form.recibio)) errors.recibio = "Ingresá quién recibió";
    else if (tooLong(form.recibio, LIMITS.recibio)) errors.recibio = `Acortá el nombre a ${LIMITS.recibio} caracteres como máximo`;
  }

  if (accion === ACCIONES.NO_ENTREGADO) {
    if (!form.motivo) errors.motivo = "Elegí el motivo";
    if (form.nuevaFecha && today && form.nuevaFecha < today) {
      errors.nuevaFecha = "La fecha estimada no puede ser anterior a hoy";
    }
  }

  checkObservacion(errors, form.observacion, accion === ACCIONES.NO_ENTREGADO && form.motivo === MOTIVO_OTRO, "Contá qué pasó");
  return errors;
};

export const validateCancelacion = (form) => {
  const errors = {};
  if (!form.motivo) errors.motivo = "Elegí el motivo";
  checkObservacion(errors, form.observacion, form.motivo === MOTIVO_OTRO, "Contá el motivo");
  return errors;
};

// Body of POST gestiones/{id}/acciones without versionToken and correlationId (the modal adds them). Date-only
// fields travel as local midnight; fecha + hora de salida travel together.
export const buildAccionPayload = (accion, form) => {
  const observacion = textOrNull(form.observacion);
  switch (accion) {
    case ACCIONES.ENVIAR:
      return { accion, cadete: text(form.cadete), fechaSalida: `${form.fechaSalida}T${form.horaSalida}:00`, observacion };
    case ACCIONES.ENTREGAR:
      return {
        accion,
        fechaEntrega: `${form.fechaEntrega}T00:00:00`,
        recibioNombre: text(form.recibio),
        cantidadEntregada: parseCantidad(form.cantidad),
        observacion,
      };
    case ACCIONES.NO_ENTREGADO:
      return {
        accion,
        motivo: form.motivo,
        nuevaFechaEstimada: form.nuevaFecha ? `${form.nuevaFecha}T00:00:00` : null,
        observacion,
      };
    case ACCIONES.CANCELAR:
      return { accion, motivo: form.motivo, observacion };
    default:
      return { accion, observacion };
  }
};

// Toast after a successful action (design RolloManagePrep "Éxito").
export const toastTitleFor = (payload) => {
  switch (payload.accion) {
    case ACCIONES.PREPARAR:
      return "Pasó a En preparación";
    case ACCIONES.ENVIAR:
      return `Enviada con ${payload.cadete}`;
    case ACCIONES.ENTREGAR:
      return "Entrega registrada · pasó a Finalizadas";
    case ACCIONES.NO_ENTREGADO:
      return "No entregada · volvió a En preparación";
    case ACCIONES.CANCELAR:
      return "Gestión cancelada · pasó a Finalizadas";
    default:
      return "Gestión actualizada";
  }
};

// Any 409 of an action means the gestión changed under the asesor (version, estado or closed): reload it.
export const isConflict = (error) => error?.status === 409;

export const CONFLICT_TITLE = "Este seguimiento fue actualizado por otro usuario.";

export const describeAccionError = (error) => ({
  title: "No pudimos guardar los cambios",
  text: error?.status
    ? error.message || "Probá de nuevo en unos segundos."
    : "No pudimos conectar con el servidor. Probá de nuevo: la acción no se va a duplicar.",
});

// ---- Timeline (GET gestiones/{id}.timeline). The server serializes "datos" with the C# property names, so the
// keys are read without caring about their case.
export const datoOf = (datos, key) => {
  if (!datos || typeof datos !== "object") return null;
  const wanted = key.toLowerCase();
  const found = Object.keys(datos).find((name) => name.toLowerCase() === wanted);
  return found === undefined ? null : datos[found];
};

const ACCION_LABELS = {
  CREAR: "Creó la gestión",
  PREPARAR: "Pasó a preparación",
  ENVIAR: "Envió con cadete",
  ENTREGAR: "Registró la entrega",
  NO_ENTREGADO: "No se pudo entregar",
  CANCELAR: "Canceló la gestión",
  TOMAR: "Tomó la gestión",
  ASIGNAR: "Asignó la gestión",
  LIBERAR: "Liberó la gestión",
};

export const accionLabel = (accion) => ACCION_LABELS[codeOf(accion)] || text(accion) || "Actualizó la gestión";

// Sentence with the data of the entry (design timeline texts); null when the action has nothing to add.
export const describeTimelineEntry = (entry, gestion) => {
  const datos = entry?.datos;
  switch (codeOf(entry?.accion)) {
    case "CREAR": {
      const canal = gestion?.canalPedido ? `, pedido vía ${labelFor(CANAL_LABELS, gestion.canalPedido)}` : "";
      return gestion?.cantidadSolicitada ? `${formatRollos(gestion.cantidadSolicitada)}${canal}.` : null;
    }
    case "ENVIAR": {
      const cadete = datoOf(datos, "cadete");
      const salida = datoOf(datos, "fechaSalida");
      if (!cadete) return null;
      const hora = formatTime(salida);
      return `Sale con ${cadete}${salida ? ` el ${formatShortDate(salida)}` : ""}${hora ? ` a las ${hora}` : ""}.`;
    }
    case "ENTREGAR": {
      const cantidad = datoOf(datos, "cantidadEntregada");
      const recibio = datoOf(datos, "recibio");
      const pedido = gestion?.cantidadSolicitada;
      const parts = [];
      if (cantidad !== null && cantidad !== undefined) {
        parts.push(`Entregó ${formatRollos(cantidad)}${pedido && Number(cantidad) !== Number(pedido) ? ` (pedido: ${pedido})` : ""}.`);
      }
      if (recibio) parts.push(`Recibió ${recibio}.`);
      return parts.length ? parts.join(" ") : null;
    }
    case "NO_ENTREGADO": {
      const nueva = datoOf(datos, "nuevaFechaEstimada");
      const motivo = entry.motivo ? `No se pudo entregar: ${labelFor(MOTIVO_NO_ENTREGA_LABELS, entry.motivo)}.` : "";
      const fecha = nueva ? ` Nueva fecha estimada: ${formatDate(nueva)}.` : "";
      return `${motivo}${fecha}`.trim() || null;
    }
    case "CANCELAR":
      return entry.motivo ? `Motivo: ${labelFor(MOTIVO_CANCELACION_LABELS, entry.motivo)}.` : null;
    default:
      return null;
  }
};

// "13/10/2026 · 08:40 · Pablo Gómez".
export const timelineWhen = (entry) =>
  [formatDate(entry?.fecha), formatTime(entry?.fecha), text(entry?.usuarioNombre)].filter(Boolean).join(" · ");

// The estado change is only drawn when there is one (ownership entries keep the same estado).
export const estadoChange = (entry) => {
  const from = codeOf(entry?.estadoAnterior);
  const to = codeOf(entry?.estadoNuevo);
  if (!to || from === to) return null;
  return { from: from && ESTADO_LABELS[from] ? from : null, to };
};

// Entries of a reloaded timeline that were not there before (highlighted after "Recargar gestión").
export const freshEntryIds = (previous, next) => {
  const known = new Set((previous || []).map((entry) => entry.id));
  return (next || []).filter((entry) => !known.has(entry.id)).map((entry) => entry.id);
};
